import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { ServiceLanding, buildLandingSchemas } from "@/components/ServiceLanding";
import { canonicalAndHreflang, ctaDesc, ctaTitle } from "@/lib/seo";
import { NAATI_VISA_SUBCLASSES, findNaatiVisaSubclass } from "@/data/naati-visa-subclasses";
import { NAATI_DOCTYPES } from "@/data/naati-doctypes";
import NaatiVisaDeepContent from "@/components/NaatiVisaDeepContent";

export const Route = createFileRoute("/translate/naati/visa/$subclass")({
  loader: ({ params }) => {
    const v = findNaatiVisaSubclass(params.subclass);
    if (!v) throw notFound();
    const docs = v.requiredDoctypes
      .map((s) => NAATI_DOCTYPES.find((d) => d.slug === s))
      .filter((d): d is NonNullable<typeof d> => !!d);
    return { v, docs };
  },
  head: ({ params }) => {
    const v = findNaatiVisaSubclass(params.subclass);
    if (!v) return { meta: [{ title: ctaTitle("Not found", "/translate/naati/visa/$subclass", "th")}, { name: "robots", content: "noindex" }] };
    const path = `/translate/naati/visa/${v.slug}`;
    const TITLE = `แปล NAATI สำหรับ ${v.nameTh} (Subclass ${v.code}) | NYC`;
    const DESC = `เอกสารไทยที่ต้องแปล NAATI สำหรับวีซ่า ${v.nameEn} (Subclass ${v.code}): ${v.requiredDoctypes.slice(0, 5).join(", ")} · ${v.processingTimeTh} · ค่าธรรมเนียม DHA AUD ${v.govFeeAud.toLocaleString} — ปรึกษาฟรี ทีมงานตอบไวทุกวัน`;
    const totalPrice = v.requiredDoctypes
      .map((s) => NAATI_DOCTYPES.find((d) => d.slug === s)?.priceFromThb ?? 0)
      .reduce((a, b) => a + b, 0);
    return {
      meta: [
        { title: ctaTitle(TITLE, "/translate/naati/visa/$subclass", "th")}, { name: "description", content: ctaDesc(DESC, "/translate/naati/visa/$subclass", "th")},
        { property: "og:title", content: ctaTitle(TITLE, "/translate/naati/visa/$subclass", "th")}, { property: "og:description", content: ctaDesc(DESC, "/translate/naati/visa/$subclass", "th")},
        { property: "og:url", content: `https://nyclanguageinstitute.com${path}` },
      ],
      links: canonicalAndHreflang(path, `/en/translate/naati/visa/${v.slug}`),
      scripts: buildLandingSchemas({
        path, title: TITLE, description: DESC,
        serviceType: `NAATI Translation — ${v.nameEn}`, priceFromThb: totalPrice,
        steps: [
          { name: "รวบรวมเอกสารต้นฉบับ", text: `${v.requiredDoctypes.length} ประเภทที่ ${v.authority} ต้องการ` },
          { name: "แปล NAATI + QR", text: "นักแปล NAATI Certified (CPN) พร้อมตราดิจิทัลอัปโหลด ImmiAccount" },
          { name: "ตรวจซ้ำ QA 2 ชั้น", text: "Editor + Reviewer ตรวจก่อนส่ง" },
          { name: "ส่งไฟล์ PDF + hardcopy", text: "EMS ฟรีทั่วไทย + ไฟล์ digitally sealed สำหรับอัปโหลด" },
          { name: "ยื่นวีซ่า", text: `ระยะเวลาพิจารณา ${v.processingTimeTh}` },
        ],
        faqs: [
          { q: `Subclass ${v.code} คืออะไร?`, a: `${v.nameTh} — ${v.streamTh}` },
          { q: "ต้องแปลกี่เอกสาร?", a: `${v.requiredDoctypes.length} ประเภทหลัก — ราคารวมประมาณ ฿${totalPrice.toLocaleString()}` },
          { q: "หน่วยงานที่พิจารณา?", a: v.authority },
          { q: "ระยะเวลา DHA พิจารณา?", a: v.processingTimeTh },
          { q: "ค่าธรรมเนียมรัฐ?", a: `AUD ${v.govFeeAud.toLocaleString()} (จ่ายที่ DHA แยกจากค่าแปล)` },
          { q: "อัปโหลดไฟล์ ImmiAccount ได้เลยไหม?", a: `ได้ — ทุกงานแปลสำหรับ subclass ${v.code} ส่งเป็นไฟล์ PDF พร้อมตราดิจิทัล NAATI QR อัปโหลดเข้า ImmiAccount ได้ทันที ไม่ต้องปริ้นต์ ไม่ต้องส่งไปรษณีย์ QR ลิงก์ตรงเข้าระบบตรวจสอบของ NAATI ให้เจ้าหน้าที่ DHA ยืนยันได้ทันที` },
          { q: "หากเอกสารเก่าเกิน 6 เดือน?", a: `DHA กำหนดให้เอกสารทะเบียนราษฎร (สูติบัตร ทะเบียนสมรส/หย่า หนังสือรับรองโสด) ต้องออกใหม่ภายใน 6 เดือน — ต้องขอคัดใหม่จากอำเภอ/เขตก่อน แล้วจึงแปล NAATI ทีมงานคัดและส่งด่วนในกรุงเทพให้ได้ เพื่อไม่ให้ subclass ${v.code} ล่าช้า` },
          { q: "รับประกันงานอย่างไร?", a: `หาก DHA ปฏิเสธเพราะข้อผิดพลาดการแปลหรือปัญหาตรา NAATI สำหรับ subclass ${v.code} เราแก้ไขและออกใหม่ให้ฟรีภายใน 24 ชั่วโมง พร้อมออกค่า DHL Express ให้เอง ส่งกลับถึงเอเจนต์วีซ่าหรือกล่องข้อความ ImmiAccount ของคุณโดยตรง` },
        ],
        breadcrumbs: [
          { name: "Home", path: "/" },
          { name: "NAATI", path: "/translate/naati" },
          { name: "By Visa", path: "/translate/naati/visa" },
          { name: `Subclass ${v.code}`, path },
        ],
      }),
    };
  },
  component: Page,
  notFoundComponent: () => (
    <main className="p-12"><h1 className="text-2xl font-bold">Subclass not found</h1></main>
  ),
});

function Page() {
  const { v, docs } = Route.useLoaderData() as { v: NonNullable<ReturnType<typeof findNaatiVisaSubclass>>; docs: typeof NAATI_DOCTYPES };
  const totalPrice = docs.reduce((a, b) => a + b.priceFromThb, 0);
  return (
    <>
      <ServiceLanding
        badge={`NAATI · Subclass ${v.code}`}
        title={`แปล NAATI สำหรับ ${v.nameTh}`}
        subtitle={`${v.streamTh} · หน่วยงาน: ${v.authority} · ระยะเวลา ${v.processingTimeTh}`}
        quickAnswer={{
          q: `ต้องแปลอะไรบ้างสำหรับ Subclass ${v.code}?`,
          a: `${v.requiredDoctypes.length} ประเภทเอกสารไทยหลัก แปลโดยนักแปล NAATI Certified (CPN) พร้อมตราดิจิทัลอัปโหลด ImmiAccount ได้ทันที ราคารวมประมาณ ฿${totalPrice.toLocaleString()} วันทำการ 2–3 วัน (Express 24 ชม.)`,
        }}
        heroBullets={[
          `✓ NAATI CPN Certified — ตรงตามข้อกำหนด ${v.authority}`,
          `✓ ${docs.length} ประเภทเอกสาร ครอบคลุมทุกจุดที่ DHA ตรวจ`,
          `✓ QR digital seal — อัปโหลด ImmiAccount ได้ทันที`,
          `✓ ระยะเวลา DHA: ${v.processingTimeTh}`,
        ]}
        whoFor={{
          th: [`ผู้ยื่น ${v.nameTh}`, "คู่สมรส/คู่ครองที่จะย้ายไป AU", "นักเรียน + Skilled worker + WHV"],
          foreign: [`Applicants of Subclass ${v.code} — ${v.nameEn}`],
        }}
        steps={v.keyPointsTh.slice(0, 5).map((t, i) => ({ name: `ขั้นตอน ${i + 1}`, text: t }))}
        pricing={docs.map((d) => ({ label: d.nameTh, price: d.priceFromThb.toLocaleString(), note: d.turnaround }))}
        faqs={[
          { q: "แหล่งอ้างอิงทางการ?", a: v.officialSource },
          { q: "หน่วยงานพิจารณา?", a: v.authority },
          { q: "ระยะเวลา?", a: v.processingTimeTh },
          { q: "ค่าธรรมเนียม DHA?", a: `AUD ${v.govFeeAud.toLocaleString()}` },
          { q: "รวมค่าแปลประมาณเท่าไร?", a: `฿${totalPrice.toLocaleString()} (Base + NAATI stamp)` },
          ...v.keyPointsTh.map((k, i) => ({ q: `ข้อควรระวัง ${i + 1}`, a: k })),
        ]}
        relatedLinks={[
          { to: "/translate/naati", label: "← NAATI hub" },
          { to: "/translate/naati/visa", label: "ดู Subclass อื่น" },
          ...NAATI_VISA_SUBCLASSES.filter((x) => x.slug !== v.slug).slice(0, 4).map((x) => ({
            to: `/translate/naati/visa/${x.slug}`, label: `Subclass ${x.code} — ${x.nameTh}`,
          })),
          { to: "/apostille", label: "Apostille (Hague ธ.ค. 2025)" },
        ]}
      />
      <section className="mx-auto max-w-5xl px-4 py-10">
        <h2 className="text-2xl font-bold">รายการเอกสารที่ต้องแปล ({docs.length})</h2>
        <div className="mt-4 overflow-x-auto rounded-2xl border">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
              <tr><th className="px-4 py-3">เอกสาร</th><th className="px-4 py-3">ราคา/หน้า</th><th className="px-4 py-3">วันทำการ</th><th className="px-4 py-3">หมายเหตุ</th></tr>
            </thead>
            <tbody>
              {docs.map((d) => (
                <tr key={d.slug} className="border-t">
                  <td className="px-4 py-3 font-semibold">
                    <Link to="/translate/naati/doctype/$slug" params={{ slug: d.slug }} className="hover:text-amber-600">{d.nameTh}</Link>
                    <div className="text-xs font-normal text-slate-500">{d.nameEn}</div>
                  </td>
                  <td className="px-4 py-3">฿{d.priceFromThb.toLocaleString()}</td>
                  <td className="px-4 py-3 text-xs">{d.turnaround}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">{d.notesTh}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <NaatiVisaDeepContent v={v} totalPrice={totalPrice} lang="th" />
    </>
  );
}
